import { effect, signal } from '@preact/signals';

import type { UiPreferences, UiPreferencesPatch } from '@amumin/api-client';

import { api, isApiError } from '../api.js';
import { context } from './session.js';

// Eigen schermkeuzes (0042_widgets; golf 6b): de indeling van Vandaag en de agendaweergave, per
// persoon op de server, zodat je telefoon en je laptop hetzelfde laten zien. Eén keer laden per
// sessie (en opnieuw na een live-poke); opslaan loopt in een rij, zodat twee snelle wijzigingen nooit
// elkaars if_version breken. Bij 409 (een ander apparaat was eerder) nemen we de versie van de server
// over en proberen we het één keer opnieuw: de laatste keuze wint, zoals de persoon het bedoelde.
//
// Review golf 6b (founder-ux r1): de optimistische keuze is de waarheid. Het antwoord op een oudere
// opslag mag een nieuwere keuze nooit terugzetten (anders rekent de volgende tik met een oude
// indeling en bewaart die). Elke opslag krijgt een volgnummer; alleen het antwoord op de nieuwste
// neemt de indeling over, de rest alleen de versie. Wie de pagina verlaat terwijl er nog iets in de
// rij staat, stuurt de nieuwste stand met `keepalive` mee (pagehide).

export const uiPrefs = signal<UiPreferences | null>(null);
/** Eerste laadpoging klaar (gelukt of niet). */
export const uiPrefsReady = signal(false);

type Patch = Omit<UiPreferencesPatch, 'if_version'>;

let inflight: Promise<UiPreferences | null> | null = null;
let queue: Promise<unknown> = Promise.resolve();
/** Laatst bekende versie op de server (antwoord van laden of opslaan); undefined = nog niet geladen. */
let serverVersion: number | null | undefined;
/** Volgnummer van de nieuwste wijziging. */
let seq = 0;
/** Opslagen die nog niet klaar zijn (in de rij of onderweg). */
let pending = 0;
/** Er is nu een verzoek onderweg naar de server. */
let sending = false;
/** Velden die sinds de laatste afgeronde opslag gewijzigd zijn (voor pagehide). */
let dirty: Patch = {};
/** De pagina gaat weg en de nieuwste stand is al met keepalive verstuurd: niets meer sturen. */
let flushed = false;

function apply(res: UiPreferences): void {
  serverVersion = res.version;
  uiPrefs.value = res;
}

export function loadUiPrefs(force = false): Promise<UiPreferences | null> {
  if (inflight && !force) return inflight;
  if (uiPrefs.value && !force) return Promise.resolve(uiPrefs.value);
  inflight = api.ui
    .get()
    .then((res) => {
      // Staat er nog een eigen wijziging in de rij, dan wint die: alleen de rest overnemen.
      if (pending > 0 && uiPrefs.value) {
        uiPrefs.value = {
          ...res,
          home: uiPrefs.value.home,
          agenda_view: uiPrefs.value.agenda_view,
        };
      } else {
        apply(res);
      }
      return uiPrefs.value;
    })
    .catch(() => uiPrefs.value)
    .finally(() => {
      uiPrefsReady.value = true;
      inflight = null;
    });
  return inflight;
}

async function versionForSave(): Promise<number | null> {
  if (serverVersion !== undefined) return serverVersion;
  const current = uiPrefs.value ?? (await loadUiPrefs());
  return current ? current.version : null;
}

async function saveOnce(patch: Patch, mine: number, retry: boolean): Promise<UiPreferences> {
  if (flushed) return uiPrefs.value as UiPreferences;
  const ifVersion = await versionForSave();
  sending = true;
  try {
    const res = await api.ui.save({ ...patch, if_version: ifVersion });
    serverVersion = res.version;
    if (mine === seq) {
      // Nieuwste wijziging: het antwoord is de stand.
      apply(res);
      dirty = {};
    } else if (uiPrefs.value) {
      // Er komt nog een nieuwere: indeling en weergave blijven zoals de persoon ze nu ziet.
      uiPrefs.value = { ...uiPrefs.value, version: res.version, home_editable: res.home_editable };
    }
    return res;
  } catch (e) {
    if (retry && isApiError(e, 409)) {
      const details = e.details as { current_version?: number | null } | undefined;
      if (details && details.current_version !== undefined) {
        serverVersion = details.current_version;
      } else {
        serverVersion = (await api.ui.get()).version;
      }
      return saveOnce(patch, mine, false);
    }
    throw e;
  } finally {
    sending = false;
  }
}

/** De eigen keuzes zijn nog niet geladen: een nieuwe indeling zou op de standaard rusten. */
export class UiPrefsNotLoaded extends Error {
  constructor() {
    super('ui_prefs_not_loaded');
  }
}

/** Keuzes opslaan (in volgorde); de signal loopt direct mee (optimistisch). */
export function saveUiPrefs(patch: Patch): Promise<UiPreferences> {
  const prev = uiPrefs.value;
  // Fixer golf 6b r2 (blocker): een indeling bewaren terwijl de eigen keuzes nog laden (of niet te
  // laden waren) zou "standaard + deze wijziging" over de bewaarde indeling zetten, zonder 409
  // (versionForSave haalt dan de huidige versie). Dus weigeren; de webapp biedt Aanpassen dan ook
  // nog niet aan.
  if (patch.home !== undefined && !prev) return Promise.reject(new UiPrefsNotLoaded());
  if (prev) {
    uiPrefs.value = {
      ...prev,
      ...(patch.home !== undefined ? { home: patch.home } : {}),
      ...(patch.agenda_view !== undefined ? { agenda_view: patch.agenda_view } : {}),
    };
  }
  dirty = { ...dirty, ...patch };
  seq += 1;
  const mine = seq;
  pending += 1;
  const run = queue.then(() => saveOnce(patch, mine, true));
  const done = () => {
    pending -= 1;
  };
  run.then(done, done);
  queue = run.catch(() => undefined);
  return run;
}

/**
 * De pagina gaat weg (herladen, andere site, app dicht) terwijl er nog een keuze in de rij staat:
 * de nieuwste stand in één verzoek met `keepalive`, zodat de browser hem ook na het sluiten nog
 * aflevert. Staat er al een verzoek onderweg, dan verwachten we de versie daarna (elke opslag
 * verhoogt de versie met één, uiRepo).
 */
function flushOnLeave(): void {
  if (pending === 0 || flushed || Object.keys(dirty).length === 0) return;
  // Alleen het verzoek dat al onderweg is: dat komt aan, niets extra's sturen.
  if (sending && pending === 1) return;
  const now = uiPrefs.value;
  if (!now || serverVersion === undefined) return;
  const body: UiPreferencesPatch = {
    ...(dirty.home !== undefined ? { home: now.home } : {}),
    ...(dirty.agenda_view !== undefined ? { agenda_view: now.agenda_view } : {}),
    if_version: sending ? (serverVersion ?? 0) + 1 : serverVersion,
    // Fixer golf 6b r2: de opslag die nog onderweg is, kan deze keepalive voorbijsteken; met
    // `leave` wint deze laatste stand altijd (de server kijkt dan niet naar if_version).
    leave: true,
  };
  flushed = true;
  try {
    void fetch('/v1/app/ui-preferences', {
      method: 'PUT',
      credentials: 'same-origin',
      keepalive: true,
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).catch(() => undefined);
  } catch {
    // Geen keepalive (oude browser): dan geldt wat al aankwam.
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushOnLeave);
  // Terug uit de bfcache na een flush: opnieuw laden, de server heeft de stand.
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted || !flushed) return;
    flushed = false;
    dirty = {};
    void loadUiPrefs(true);
  });
}

/** Na uitloggen of een ander huishouden: alles vergeten. */
export function resetUiPrefs(): void {
  uiPrefs.value = null;
  uiPrefsReady.value = false;
  inflight = null;
  serverVersion = undefined;
  dirty = {};
}

// Andere login of ander huishouden: de keuzes van de vorige horen er niet meer bij.
let owner = '';
effect(() => {
  const ctx = context.value;
  const key = ctx ? `${ctx.identity.id}:${ctx.active_household_id ?? ''}` : '';
  if (key !== owner) {
    owner = key;
    resetUiPrefs();
  }
});
